import { useId, useMemo, useState } from 'react'
import {
  SALES_CHANNELS,
  channelMetricValue,
  pointDivisor,
  pointMetricValue,
  type SalesChannel,
  type SalesGrouping,
  type SalesMetric,
  type SalesPoint,
  type SalesScale,
} from '../dashboard/salesAnalysis'
import { formatCurrency, formatNumber } from '../lib/format'

const HEIGHT = 280
const MARGIN = { top: 16, right: 24, bottom: 60, left: 68 }

function niceCeiling(value: number): number {
  const step =
    value <= 20 ? 5 : value <= 100 ? 10 : value <= 500 ? 50 : value <= 1000 ? 100 : value <= 5000 ? 500 : 1000
  return Math.max(step, Math.ceil(value / step) * step)
}

function formatMetric(value: number, metric: SalesMetric): string {
  return metric === 'orders' ? formatNumber(value, 0) : formatCurrency(value)
}

function axisMetric(value: number, metric: SalesMetric): string {
  if (metric === 'orders') return formatNumber(value, 0)
  if (Math.abs(value) >= 1000) return `€${formatNumber(value / 1000, 1)}k`
  return `€${formatNumber(value, 0)}`
}

function metricColor(metric: SalesMetric): string {
  if (metric === 'refunds') return '#e11d48'
  if (metric === 'orders') return '#0ea5e9'
  if (metric === 'aov') return '#8b5cf6'
  return '#f97316'
}

/**
 * Interactive Sales Analysis columns.
 *
 * Additive metrics stack by channel; AOV switches to grouped channel bars so
 * three averages are never added together. Clicking a bucket pins it. The
 * detail strip is deliberately outside the SVG so it stays readable while a
 * long date series scrolls horizontally.
 */
export function SalesAnalysisChart({
  points,
  metric,
  grouping,
  scale,
  days,
  byChannel,
}: {
  points: SalesPoint[]
  metric: SalesMetric
  grouping: SalesGrouping
  scale: SalesScale
  days: number
  byChannel: boolean
}) {
  const [hovered, setHovered] = useState<number | null>(null)
  const [pinned, setPinned] = useState<number | null>(null)
  const gradientId = useId().replace(/:/g, '')
  const isGroupedAov = byChannel && metric === 'aov'

  const width = Math.max(
    700,
    points.length * (points.length > 180 ? 12 : 34) + MARGIN.left + MARGIN.right,
  )
  const plotWidth = width - MARGIN.left - MARGIN.right
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom
  const yValues = useMemo(
    () =>
      points.flatMap((point) => {
        const divisor = pointDivisor(point, grouping, scale, days)
        return byChannel && metric === 'aov'
          ? SALES_CHANNELS.map((channel) =>
              channelMetricValue(point, channel.key, metric, divisor),
            )
          : [pointMetricValue(point, metric, divisor)]
      }),
    [points, byChannel, metric, grouping, scale, days],
  )
  const yMax = niceCeiling(Math.max(...yValues, 1))
  const slot = points.length > 0 ? plotWidth / points.length : plotWidth
  const barWidth = Math.min(34, slot * 0.64)
  const channelShares = useMemo(() => {
    const totals = Object.fromEntries(
      SALES_CHANNELS.map((channel) => [channel.key, 0]),
    ) as Record<SalesChannel, number>
    let salesTotal = 0
    for (const point of points) {
      for (const channel of SALES_CHANNELS) {
        const sales = point.channels[channel.key].sales
        totals[channel.key] += sales
        salesTotal += sales
      }
    }
    return Object.fromEntries(
      SALES_CHANNELS.map((channel) => [
        channel.key,
        salesTotal > 0 ? (totals[channel.key] / salesTotal) * 100 : 0,
      ]),
    ) as Record<SalesChannel, number>
  }, [points])
  const x = (index: number) => MARGIN.left + slot * (index + 0.5)
  const y = (value: number) => MARGIN.top + plotHeight - (Math.max(0, value) / yMax) * plotHeight
  const baseY = MARGIN.top + plotHeight
  const yTicks = Array.from({ length: 5 }, (_, index) => (yMax / 4) * index)
  const labelEvery = points.length <= 12 ? 1 : Math.ceil(points.length / 12)
  const activeIndex = Math.min(hovered ?? pinned ?? 0, points.length - 1)
  const active = points[activeIndex] ?? points[0]
  const topIndex = useMemo(() => {
    let best = -1
    let bestValue = -Infinity
    points.forEach((point, index) => {
      const value = pointMetricValue(
        point,
        metric,
        pointDivisor(point, grouping, scale, days),
      )
      if (value > bestValue) {
        bestValue = value
        best = index
      }
    })
    return best
  }, [points, metric, grouping, scale, days])
  const topPoint = topIndex >= 0 ? points[topIndex] : null
  const topValue =
    topPoint !== null
      ? pointMetricValue(topPoint, metric, pointDivisor(topPoint, grouping, scale, days))
      : 0
  const topBucketLabel =
    grouping === 'hour'
      ? 'Top hour'
      : grouping === 'weekday'
        ? 'Top weekday'
        : grouping === 'week'
          ? 'Top week'
          : grouping === 'month'
            ? 'Top month'
            : grouping === 'quarter'
              ? 'Top quarter'
              : 'Top day'

  if (points.length === 0 || !active) {
    return (
      <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-slate-200 text-xs text-slate-400 dark:border-white/10 dark:text-slate-500">
        No sales columns for this selection.
      </div>
    )
  }

  const selectPoint = (index: number) => {
    setPinned((current) => (current === index ? null : index))
    setHovered(index)
  }

  const activeTotal = pointMetricValue(
    active,
    metric,
    pointDivisor(active, grouping, scale, days),
  )
  const scaleLabel =
    grouping === 'hour'
      ? scale === 'average'
        ? 'Average per day'
        : 'Total across period'
      : grouping === 'weekday'
        ? scale === 'average'
          ? 'Average per weekday'
          : 'Total across period'
        : 'Total per bucket'

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-slate-200/80 bg-slate-50/60 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.03]">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            {active.label} {active.detail ? `· ${active.detail}` : ''}
          </p>
          <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
            {formatMetric(activeTotal, metric)}
            <span className="ml-1.5 text-[11px] font-normal text-slate-400 dark:text-slate-500">
              {metric === 'sales'
                ? 'sales'
                : metric === 'orders'
                  ? 'orders'
                  : metric === 'aov'
                    ? 'AOV'
                    : 'refunds'}
            </span>
          </p>
          {topPoint && (
            <span className="mt-1.5 inline-flex max-w-full items-center gap-1.5 rounded-full bg-salsa-500/10 px-2 py-0.5 text-[10px] font-semibold text-salsa-700 ring-1 ring-salsa-500/15 dark:text-salsa-300 dark:ring-salsa-500/25">
              <span className="h-1.5 w-1.5 rounded-full bg-salsa-500" aria-hidden="true" />
              <span className="truncate">
                {topBucketLabel} · {topPoint.label}
              </span>
              <span className="tabular-nums text-salsa-800/80 dark:text-salsa-200/80">
                {formatMetric(topValue, metric)}
              </span>
            </span>
          )}
        </div>
        {byChannel && (
          <div className="flex min-w-0 flex-col items-end gap-1.5">
            <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[10px] text-slate-500 dark:text-slate-400">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Share of sales
              </span>
              {SALES_CHANNELS.map((channel) => (
                <span
                  key={`share-${channel.key}`}
                  title={`${channel.label}: ${formatNumber(channelShares[channel.key], 1)}% of sales`}
                  aria-label={`${channel.label} ${formatNumber(channelShares[channel.key], 1)}% of sales`}
                  className="inline-flex items-center gap-1"
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: channel.color }} />
                  {channel.label}
                  <span className="font-semibold text-slate-600 dark:text-slate-300">
                    {formatNumber(channelShares[channel.key], 1)}%
                  </span>
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 text-right">
              {SALES_CHANNELS.map((channel) => (
                <span
                  key={channel.key}
                  title={`${channel.label}: ${formatNumber(channelShares[channel.key], 1)}% of sales`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2 py-1 text-[11px] tabular-nums text-slate-600 shadow-sm ring-1 ring-slate-200/70 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10"
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: channel.color }} />
                  <span className="font-semibold text-slate-700 dark:text-slate-200">{channel.label}</span>
                  {formatMetric(
                    channelMetricValue(
                      active,
                      channel.key,
                      metric,
                      pointDivisor(active, grouping, scale, days),
                    ),
                    metric,
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="overflow-x-auto pb-1">
        <svg
          viewBox={`0 0 ${width} ${HEIGHT}`}
          style={{
            width: `${width}px`,
            minWidth: `${width}px`,
            maxWidth: '100%',
          }}
          className="mx-auto h-auto overflow-visible"
          role="img"
          aria-label={`Sales analysis by ${grouping}, ${scaleLabel}`}
          onMouseLeave={() => setHovered(null)}
        >
          <title>{`Sales analysis by ${grouping}`}</title>
          <desc>
            Interactive {metric} columns for the selected shops and period. Hover a column to inspect it.
          </desc>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={metricColor(metric)} stopOpacity="0.95" />
              <stop offset="100%" stopColor={metricColor(metric)} stopOpacity="0.5" />
            </linearGradient>
          </defs>

          {yTicks.map((tick) => (
            <g key={`y-${tick}`}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={y(tick)}
                y2={y(tick)}
                className="stroke-slate-200/80 dark:stroke-white/10"
                strokeDasharray={tick === 0 ? undefined : '3 5'}
              />
              <text
                x={MARGIN.left - 12}
                y={y(tick) + 4}
                textAnchor="end"
                className="fill-slate-400 text-[11px] tabular-nums dark:fill-slate-500"
              >
                {axisMetric(tick, metric)}
              </text>
            </g>
          ))}
          {topIndex >= 0 && (
            <g pointerEvents="none" aria-hidden="true">
              <rect
                x={MARGIN.left + slot * topIndex + 2}
                y={MARGIN.top}
                width={Math.max(0, slot - 4)}
                height={plotHeight}
                rx={7}
                className="fill-salsa-500/[0.06] dark:fill-salsa-500/[0.08]"
              />
              {!isGroupedAov && (
                <>
                  <line
                    x1={x(topIndex)}
                    x2={x(topIndex)}
                    y1={MARGIN.top - 5}
                    y2={Math.max(MARGIN.top + 4, y(topValue) - 8)}
                    stroke={metricColor(metric)}
                    strokeWidth={1.5}
                    strokeDasharray="2 4"
                    opacity={0.45}
                  />
                  <circle
                    cx={x(topIndex)}
                    cy={MARGIN.top - 5}
                    r={3}
                    fill={metricColor(metric)}
                  />
                </>
              )}
            </g>
          )}
          {points.map((point, index) => {
            const isActive = index === activeIndex
            return (
              <g key={point.key}>
                {isActive && (
                  <rect
                    x={MARGIN.left + slot * index + 2}
                    y={MARGIN.top}
                    width={Math.max(0, slot - 4)}
                    height={plotHeight}
                    rx={7}
                    className="fill-slate-900/[0.035] dark:fill-white/[0.045]"
                  />
                )}
                <text
                  x={x(index)}
                  y={HEIGHT - 45}
                  textAnchor="end"
                  transform={`rotate(-38 ${x(index)} ${HEIGHT - 45})`}
                  className={`fill-slate-400 text-[10px] tabular-nums dark:fill-slate-500 ${
                    isActive ? 'font-semibold fill-slate-600 dark:fill-slate-300' : ''
                  }`}
                >
                  {index % labelEvery === 0 || index === points.length - 1 ? point.label : ''}
                </text>
              </g>
            )
          })}
          {points.map((point, index) => {
            if (!isGroupedAov) {
              let cursor = baseY
              let cumulative = 0
              const pointScale = pointDivisor(point, grouping, scale, days)
              const total = pointMetricValue(point, metric, pointScale)
              if (!byChannel) {
                return (
                  <rect
                    key={`bar-${point.key}`}
                    x={x(index) - barWidth / 2}
                    y={y(total)}
                    width={barWidth}
                    height={Math.max(1, baseY - y(total))}
                    rx={5}
                    fill={`url(#${gradientId})`}
                    className="cursor-pointer transition-opacity hover:opacity-80"
                    onMouseEnter={() => setHovered(index)}
                    onClick={() => selectPoint(index)}
                  />
                )
              }
              return SALES_CHANNELS.map((channel) => {
                const value = channelMetricValue(point, channel.key, metric, pointScale)
                cumulative += value
                const top = y(cumulative)
                const height = Math.max(0, cursor - top)
                cursor = top
                if (height <= 0) return null
                return (
                  <rect
                    key={`${point.key}-${channel.key}`}
                    x={x(index) - barWidth / 2}
                    y={top}
                    width={barWidth}
                    height={height}
                    rx={2}
                    fill={channel.color}
                    className="cursor-pointer transition-opacity hover:opacity-80"
                    onMouseEnter={() => setHovered(index)}
                    onClick={() => selectPoint(index)}
                  />
                )
              })
            }
            const channelWidth = Math.max(3, (barWidth - 6) / SALES_CHANNELS.length)
            const pointScale = pointDivisor(point, grouping, scale, days)
            return SALES_CHANNELS.map((channel, channelIndex) => {
              const value = channelMetricValue(point, channel.key, metric, pointScale)
              return (
                <rect
                  key={`${point.key}-${channel.key}`}
                  x={x(index) - barWidth / 2 + channelIndex * (channelWidth + 2)}
                  y={y(value)}
                  width={channelWidth}
                  height={Math.max(1, baseY - y(value))}
                  rx={3}
                  fill={channel.color}
                  className="cursor-pointer transition-opacity hover:opacity-80"
                  onMouseEnter={() => setHovered(index)}
                  onClick={() => selectPoint(index)}
                />
              )
            })
          })}

          {points.map((point, index) => (
            <rect
              key={`hit-${point.key}`}
              x={MARGIN.left + slot * index}
              y={MARGIN.top}
              width={slot}
              height={plotHeight}
              fill="transparent"
              className="cursor-pointer"
              onMouseEnter={() => setHovered(index)}
              onClick={() => selectPoint(index)}
            />
          ))}

          <text
            x={MARGIN.left + plotWidth / 2}
            y={HEIGHT - 8}
            textAnchor="middle"
            className="fill-slate-400 text-[11px] font-semibold uppercase tracking-wider dark:fill-slate-500"
          >
            {grouping === 'weekday' ? 'Day of week' : grouping === 'hour' ? 'Hour of day' : 'Period'}
          </text>
        </svg>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-400 dark:text-slate-500">
        <span>
          {scaleLabel} · click a column to pin it · {points.length} buckets
        </span>
        <div className="flex flex-wrap items-center gap-3">
          {!byChannel && (
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: metricColor(metric) }} />
              All channels
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
